import { useState, useEffect } from 'react';
import { validateTimeRange } from '../utilities/timeValidation';
import { calculateEndTime } from '../utilities/timeValidation';

interface UseAnnotationTimeProps {
  currentTime: number;
  duration: number;
  onSeek: (time: number) => void;
  enabled?: boolean;
}

export function useAnnotationTime({ 
  currentTime, 
  duration,
  onSeek,
  enabled = true
}: UseAnnotationTimeProps) {
  const [startTime, setStartTime] = useState(currentTime);
  const [endTime, setEndTime] = useState(calculateEndTime(currentTime, duration));
  const [error, setError] = useState<string | null>(null);

  // Reset when disabled
  useEffect(() => {
    if (!enabled) {
      setStartTime(0);
      setEndTime(0);
      setError(null);
    }
  }, [enabled]);

  // Update times when video time changes and enabled
  useEffect(() => {
    if (enabled) {
      const newStartTime = currentTime;
      const newEndTime = calculateEndTime(currentTime, duration);
      
      const validation = validateTimeRange(newStartTime, newEndTime, duration);
      if (validation.isValid) {
        setStartTime(newStartTime);
        setEndTime(newEndTime);
        setError(null);
      } else {
        setError(validation.message || 'Invalid time range');
      }
    }
  }, [currentTime, duration, enabled]);

  const updateStartTime = (time: number) => {
    if (!enabled) return;
    
    const newEndTime = calculateEndTime(time, duration);
    const validation = validateTimeRange(time, newEndTime, duration);
    
    if (validation.isValid) {
      setStartTime(time);
      setEndTime(newEndTime);
      setError(null);
      onSeek(time);
    } else {
      setError(validation.message || 'Invalid time range');
    }
  };

  const updateEndTime = (time: number) => {
    if (!enabled) return;
    
    const validation = validateTimeRange(startTime, time, duration);
    if (validation.isValid) {
      setEndTime(time);
      setError(null);
    } else {
      setError(validation.message || 'Invalid time range');
    }
  };

  return {
    startTime: enabled ? startTime : undefined,
    endTime: enabled ? endTime : undefined,
    error,
    updateStartTime,
    updateEndTime
  };
}